import React, { useState } from 'react';
import {
  Card,
  Typography,
  Space,
  Button,
  Alert,
  Row,
  Col,
  Spin,
  Modal,
  message,
  Form,
  Input,
  Switch,
  Table,
  Tag,
  Popconfirm,
  InputNumber,
} from 'antd';
import {
  BarChartOutlined,
  ReloadOutlined,
  SettingOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import {
  useGetSystemSettingsByCategoryQuery,
  useUpdateSystemSettingMutation,
  useCalculateBenchmarksMutation,
  useGetAdminBenchmarkBusinessTypesQuery,
  useCreateBenchmarkBusinessTypeMutation,
  useUpdateBenchmarkBusinessTypeMutation,
  useDeleteBenchmarkBusinessTypeMutation,
  useGetAdminBenchmarkProductTypesQuery,
  useCreateBenchmarkProductTypeMutation,
  useUpdateBenchmarkProductTypeMutation,
  useDeleteBenchmarkProductTypeMutation,
  useSetBusinessTypeProductTypesMutation,
} from '../../../services/nestjs-base-api';
import { useAuth } from '../../../hooks/useAuth';

const { Paragraph, Text } = Typography;
const { confirm } = Modal;

export const BenchmarkingTab: React.FC = () => {
  const [form] = Form.useForm();
  const { user } = useAuth();
  const jwtToken = user?.jwtToken || '';

  // RTK Query hooks
  const { data: categorizedData, isLoading: loading, refetch } = useGetSystemSettingsByCategoryQuery({ jwtToken });
  const [updateSetting] = useUpdateSystemSettingMutation();
  const [calculateBenchmarks, { isLoading: calculating }] = useCalculateBenchmarksMutation();

  // Flatten all settings from categories
  const allSettings = React.useMemo(() => {
    if (!categorizedData?.categories) return [];
    return categorizedData.categories.flatMap(category => category.settings);
  }, [categorizedData]);

  // Filter benchmarking settings
  const benchmarkSettings = allSettings.filter((s: any) =>
    s.name.startsWith('BENCHMARK_')
  );

  const getSetting = (name: string) => {
    return benchmarkSettings.find((s: any) => s.name === name);
  };

  const handleUpdateSetting = async (name: string, value: string) => {
    try {
      const setting = getSetting(name);
      if (!setting) return;

      await updateSetting({
        jwtToken,
        settingName: setting.name,
        payload: { value },
      }).unwrap();

      message.success('Setting updated successfully');
      refetch();
    } catch (error: any) {
      message.error(error?.data?.message || 'Failed to update setting');
    }
  };

  const handleRefreshBenchmarks = async () => {
    confirm({
      title: 'Recalculate All Benchmarks?',
      icon: <BarChartOutlined />,
      content: (
        <div>
          <p>This will:</p>
          <ul>
            <li>Recalculate brand equity scores for all brands</li>
            <li>Update benchmark data points</li>
            <li>Regenerate statistics for all segments</li>
          </ul>
          <p><strong>This operation may take several minutes.</strong></p>
          <p>Are you sure you want to proceed?</p>
        </div>
      ),
      onOk: async () => {
        try {
          await calculateBenchmarks({ jwtToken, forceRecalculation: true }).unwrap();
          message.success('Benchmarks recalculated successfully');
          refetch();
        } catch (error: any) {
          message.error(error?.data?.message || 'Failed to recalculate benchmarks');
        }
      },
    });
  };

  const includeSelf = getSetting('BENCHMARK_INCLUDE_SELF');
  const minSampleSize = getSetting('BENCHMARK_MIN_SAMPLE_SIZE');
  const minResponseCount = getSetting('BENCHMARK_MIN_RESPONSE_COUNT');
  const recalcInterval = getSetting('BENCHMARK_RECALCULATION_INTERVAL_HOURS');
  const includeNegative = getSetting('BENCHMARK_INCLUDE_NEGATIVE_VALUES');

  return (
    <div>
      <Alert
        message="Benchmarking Configuration"
        description="Configure how brand benchmarking statistics are calculated and displayed. These settings affect data pool segregation, minimum sample sizes, and recalculation intervals. Benchmarks are automatically recalculated daily at 2 AM."
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Text type="secondary">
            Manage benchmarking calculation settings and trigger manual recalculation
          </Text>
        </div>
        <Button
          icon={<ReloadOutlined style={{ marginRight: 4 }} />}
          onClick={() => refetch()}
          loading={loading}
        >
          Refresh Settings
        </Button>
      </div>

      {loading ? (
        <Spin />
      ) : (
        <Row gutter={[24, 24]}>
          {/* Statistical Requirements */}
          <Col xs={24} lg={12}>
            <Card title={<><TeamOutlined style={{ marginRight: 8 }} />Data Quality Requirements</>} style={{ height: '100%' }}>
              <Form layout="vertical">
                <Form.Item
                  label="Minimum Sample Size (Brands)"
                  help="Minimum number of unique brands required for reliable statistics"
                >
                  <Input
                    type="number"
                    min={2}
                    max={10}
                    value={minSampleSize?.value}
                    onChange={(e) => handleUpdateSetting('BENCHMARK_MIN_SAMPLE_SIZE', e.target.value)}
                  />
                </Form.Item>

                <Form.Item
                  label="Minimum Response Count"
                  help="Minimum survey responses per brand per time period (ensures statistical validity)"
                >
                  <Input
                    type="number"
                    min={10}
                    max={100}
                    value={minResponseCount?.value}
                    onChange={(e) => handleUpdateSetting('BENCHMARK_MIN_RESPONSE_COUNT', e.target.value)}
                  />
                </Form.Item>

                <Alert
                  message="Statistical Validity"
                  description={`With ${minResponseCount?.value || 30} responses minimum, benchmarks meet Central Limit Theorem requirements for normal distribution.`}
                  type="success"
                  showIcon
                  icon={<CheckCircleOutlined />}
                />
              </Form>
            </Card>
          </Col>

          {/* Calculation Behavior */}
          <Col xs={24} lg={12}>
            <Card title={<><SettingOutlined style={{ marginRight: 8 }} />Calculation Behavior</>} style={{ height: '100%' }}>
              <Form layout="vertical">
                <Form.Item
                  label="Recalculation Interval (Hours)"
                  help="Hours to wait before recalculating statistics for the same segment"
                >
                  <Input
                    type="number"
                    min={1}
                    max={168}
                    value={recalcInterval?.value}
                    onChange={(e) => handleUpdateSetting('BENCHMARK_RECALCULATION_INTERVAL_HOURS', e.target.value)}
                  />
                </Form.Item>

                <Form.Item
                  label="Include Brand in Own Benchmarks"
                  help="Whether a brand's data should be included when calculating its benchmarks"
                >
                  <Switch
                    checked={includeSelf?.value === 'true'}
                    onChange={(checked) => handleUpdateSetting('BENCHMARK_INCLUDE_SELF', checked ? 'true' : 'false')}
                  />
                </Form.Item>

                <Form.Item
                  label="Include Negative Values"
                  help="Include all performance levels (including negative scores) in statistics"
                >
                  <Switch
                    checked={includeNegative?.value === 'true'}
                    onChange={(checked) => handleUpdateSetting('BENCHMARK_INCLUDE_NEGATIVE_VALUES', checked ? 'true' : 'false')}
                  />
                </Form.Item>
              </Form>
            </Card>
          </Col>

          {/* Manual Recalculation */}
          <Col xs={24}>
            <Card title="Manual Recalculation">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Paragraph>
                  Trigger immediate recalculation of all benchmark statistics. This will process all brands and regenerate statistics for all segments, respecting the current configuration.
                </Paragraph>

                <Alert
                  message="Performance Impact"
                  description="Manual recalculation processes all brands and can take several minutes. Scheduled automatic recalculation is recommended for regular updates."
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                />

                <Button
                  type="primary"
                  size="large"
                  icon={<BarChartOutlined style={{ marginRight: 4 }} />}
                  onClick={handleRefreshBenchmarks}
                  loading={calculating}
                  block
                >
                  Recalculate All Benchmarks
                </Button>
              </Space>
            </Card>
          </Col>

          {/* Benchmark Groups Configuration */}
          <Col xs={24}>
            <BenchmarkGroupsSection jwtToken={jwtToken} />
          </Col>
        </Row>
      )}
    </div>
  );
};

// --- Benchmark Groups Section ---

interface GroupItem {
  id: number;
  name: string;
  slug: string;
  displayOrder: number;
  isActive: boolean;
  productTypes?: GroupItem[];
}

const BenchmarkGroupsSection: React.FC<{ jwtToken: string }> = ({ jwtToken }) => {
  const { data: businessTypes, isLoading: btLoading } = useGetAdminBenchmarkBusinessTypesQuery({ jwtToken });
  const [createBT] = useCreateBenchmarkBusinessTypeMutation();
  const [updateBT] = useUpdateBenchmarkBusinessTypeMutation();
  const [deleteBT] = useDeleteBenchmarkBusinessTypeMutation();
  const [setProductTypes] = useSetBusinessTypeProductTypesMutation();

  const { data: productTypes, isLoading: ptLoading } = useGetAdminBenchmarkProductTypesQuery({ jwtToken });
  const [createPT] = useCreateBenchmarkProductTypeMutation();
  const [updatePT] = useUpdateBenchmarkProductTypeMutation();
  const [deletePT] = useDeleteBenchmarkProductTypeMutation();

  const [editingItem, setEditingItem] = useState<{ type: 'business' | 'product'; item: GroupItem | null } | null>(null);
  const [modalForm] = Form.useForm();

  const handleSave = async (type: 'business' | 'product') => {
    try {
      const values = await modalForm.validateFields();
      if (editingItem?.item) {
        if (type === 'business') {
          await updateBT({ jwtToken, id: editingItem.item.id, data: values }).unwrap();
        } else {
          await updatePT({ jwtToken, id: editingItem.item.id, data: values }).unwrap();
        }
        message.success('Updated successfully');
      } else {
        if (type === 'business') {
          await createBT({ jwtToken, data: values }).unwrap();
        } else {
          await createPT({ jwtToken, data: values }).unwrap();
        }
        message.success('Created successfully');
      }
      setEditingItem(null);
      modalForm.resetFields();
    } catch (error: any) {
      message.error(error?.data?.message || 'Failed to save');
    }
  };

  const handleDelete = async (type: 'business' | 'product', id: number) => {
    try {
      if (type === 'business') {
        await deleteBT({ jwtToken, id }).unwrap();
      } else {
        await deletePT({ jwtToken, id }).unwrap();
      }
      message.success('Deleted successfully');
    } catch (error: any) {
      message.error(error?.data?.message || 'Failed to delete');
    }
  };

  const handleToggleActive = async (type: 'business' | 'product', item: GroupItem) => {
    try {
      if (type === 'business') {
        await updateBT({ jwtToken, id: item.id, data: { isActive: !item.isActive } }).unwrap();
      } else {
        await updatePT({ jwtToken, id: item.id, data: { isActive: !item.isActive } }).unwrap();
      }
    } catch (error: any) {
      message.error(error?.data?.message || 'Failed to update');
    }
  };

  const handleToggleProductTypeLink = async (businessType: GroupItem, productTypeId: number) => {
    const currentIds = (businessType.productTypes || []).map((pt: GroupItem) => pt.id);
    const newIds = currentIds.includes(productTypeId)
      ? currentIds.filter((id: number) => id !== productTypeId)
      : [...currentIds, productTypeId];
    try {
      await setProductTypes({ jwtToken, businessTypeId: businessType.id, productTypeIds: newIds }).unwrap();
    } catch (error: any) {
      message.error(error?.data?.message || 'Failed to update product type link');
    }
  };

  const openModal = (type: 'business' | 'product', item?: GroupItem) => {
    setEditingItem({ type, item: item || null });
    if (item) {
      modalForm.setFieldsValue(item);
    } else {
      modalForm.resetFields();
    }
  };

  const baseColumns = (type: 'business' | 'product') => [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      key: 'slug',
      render: (slug: string) => <Tag>{slug}</Tag>,
    },
    {
      title: 'Order',
      dataIndex: 'displayOrder',
      key: 'displayOrder',
      width: 60,
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 70,
      render: (isActive: boolean, record: GroupItem) => (
        <Switch
          checked={isActive}
          size="small"
          onChange={() => handleToggleActive(type, record)}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_: any, record: GroupItem) => (
        <Space>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => openModal(type, record)}
          />
          <Popconfirm
            title="Delete this item?"
            description="Brands using this type will lose their assignment."
            onConfirm={() => handleDelete(type, record.id)}
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Business type table has an extra "Product Types" column
  const businessTypeColumns = [
    ...baseColumns('business').slice(0, 2), // Name, Slug
    {
      title: 'Product Types',
      key: 'productTypes',
      render: (_: any, record: GroupItem) => {
        const linkedIds = (record.productTypes || []).map((pt: GroupItem) => pt.id);
        return (
          <Space size={4} wrap>
            {(productTypes || []).map((pt: GroupItem) => {
              const isLinked = linkedIds.includes(pt.id);
              return (
                <Tag
                  key={pt.id}
                  color={isLinked ? 'blue' : undefined}
                  style={{ cursor: 'pointer', opacity: isLinked ? 1 : 0.45 }}
                  onClick={() => handleToggleProductTypeLink(record, pt.id)}
                >
                  {pt.name}
                </Tag>
              );
            })}
          </Space>
        );
      },
    },
    ...baseColumns('business').slice(2), // Order, Active, Actions
  ];

  const productTypeColumns = baseColumns('product');

  return (
    <Card
      title={<><AppstoreOutlined style={{ marginRight: 8 }} />Benchmark Groups</>}
    >
      <Alert
        message="Benchmark Dimensions"
        description="Business types and product types define the benchmarking comparison pools. Brands are assigned a business type (e.g. D2C, 3P Retail) and product type (e.g. Durable, Perishable). Users can filter benchmarks by these dimensions."
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Row gutter={[24, 24]}>
        <Col xs={24}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Text strong>Business Types</Text>
            <Button
              type="primary"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => openModal('business')}
            >
              Add
            </Button>
          </div>
          <Table
            dataSource={businessTypes || []}
            columns={businessTypeColumns}
            rowKey="id"
            size="small"
            pagination={false}
            loading={btLoading}
          />
        </Col>

        <Col xs={24} lg={12}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Text strong>Product Types</Text>
            <Button
              type="primary"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => openModal('product')}
            >
              Add
            </Button>
          </div>
          <Table
            dataSource={productTypes || []}
            columns={productTypeColumns}
            rowKey="id"
            size="small"
            pagination={false}
            loading={ptLoading}
          />
        </Col>
      </Row>

      <Modal
        title={editingItem?.item ? `Edit ${editingItem.type === 'business' ? 'Business' : 'Product'} Type` : `Add ${editingItem?.type === 'business' ? 'Business' : 'Product'} Type`}
        open={!!editingItem}
        onOk={() => editingItem && handleSave(editingItem.type)}
        onCancel={() => { setEditingItem(null); modalForm.resetFields(); }}
        destroyOnClose
      >
        <Form form={modalForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
            <Input placeholder="e.g. D2C or Durable Goods" />
          </Form.Item>
          <Form.Item name="slug" label="Slug" rules={[{ required: true, message: 'Slug is required' }]}>
            <Input placeholder="e.g. d2c or durable" />
          </Form.Item>
          <Form.Item name="displayOrder" label="Display Order">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};
